import { useCallback } from "react";

import { EmptystateHelmetColored, EmptystateMagnetColored } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Tooltip from "ds/components/Tooltip";
import { Action, IntentProject } from "types/generated";
import PageLoading from "components/loading/PageLoading";
import useErrorHandle from "hooks/useErrorHandle";
import useIntentProjectPolicies from "shared/IntentProject/useIntentProjectPolicies";
import { checkAllowedAction } from "utils/access";
import useTitle from "hooks/useTitle";
import CollapsibleSection from "ds/components/CollapsibleSection";
import Counter from "ds/components/Counter";

import { showIntentProjectPoliciesAttachDrawer } from "./AttachDrawer";
import { EMPTY_STATE_CAPTION } from "./constants";
import IntentProjectPoliciesListItem from "./ListItem";

type IntentProjectPoliciesProps = {
  intentProject: IntentProject;
};

const IntentProjectPolicies = ({ intentProject }: IntentProjectPoliciesProps) => {
  useTitle(`Policies · ${intentProject.name}`);

  const { manuallyAttachedPolicies, autoAttachedPolicies, attachablePolicies, loading, error } =
    useIntentProjectPolicies(intentProject.id);

  const ErrorContent = useErrorHandle(error);

  const canAttachPolicy = checkAllowedAction(intentProject, Action.IntentProjectPolicyAttach);

  const handleAttachAction = useCallback(() => {
    showIntentProjectPoliciesAttachDrawer({
      entityId: intentProject.id,
      attachablePolicies,
    });
  }, [intentProject.id, attachablePolicies]);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading) {
    return <PageLoading />;
  }

  return (
    <>
      <PageInfoHeader title="Policies">
        <Tooltip
          on={(props) => (
            <Button
              {...props}
              variant="primary"
              onPress={handleAttachAction}
              disabled={!canAttachPolicy.result}
            >
              Attach policy
            </Button>
          )}
          active={!!canAttachPolicy.message}
        >
          {canAttachPolicy.message}
        </Tooltip>
      </PageInfoHeader>

      <Box direction="column" gap="x-large">
        <CollapsibleSection
          ariaLevel={3}
          ariaLabel="Attached directly"
          title="Attached directly"
          contentGap="medium"
          titleSuffix={<Counter count={manuallyAttachedPolicies.length} />}
          initialIsCollapsed={false}
        >
          {manuallyAttachedPolicies.map((policy) => (
            <IntentProjectPoliciesListItem
              key={policy.id}
              policy={policy}
              intentProject={intentProject}
            />
          ))}

          {manuallyAttachedPolicies.length === 0 && (
            <EmptyState
              padding="large"
              icon={EmptystateHelmetColored}
              title="No policies attached yet."
              caption={EMPTY_STATE_CAPTION}
            />
          )}
        </CollapsibleSection>

        <CollapsibleSection
          ariaLevel={3}
          ariaLabel="Auto-attached"
          title="Auto-attached"
          contentGap="medium"
          titleSuffix={<Counter count={autoAttachedPolicies.length} />}
          initialIsCollapsed={false}
        >
          {autoAttachedPolicies.map((policy) => (
            <IntentProjectPoliciesListItem
              key={policy.id}
              policy={policy}
              intentProject={intentProject}
            />
          ))}

          {autoAttachedPolicies.length === 0 && (
            <EmptyState
              padding="large"
              icon={EmptystateMagnetColored}
              title="No policies auto-attached yet."
              caption={EMPTY_STATE_CAPTION}
            />
          )}
        </CollapsibleSection>
      </Box>
    </>
  );
};

export default IntentProjectPolicies;
